import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { qk } from '@/api/queries';
import type { PresetDetail, PresetVersion } from '@/api/types';
import { paths } from '@/app/paths';
import { useSession } from '@/app/session';
import {
  Button, CapsLabel, Card, COLOR_MODES, Drawer, FieldHint, IconButton, Input, isLayout, MIRRORABLE, Pill, Segmented, sectionType, Tag, Textarea, Thumb,
  useToast, type ColorMode, type Tone,
} from '@/design-system';
import { cx } from '@/lib/cx';
import { formatDateShort, formatTime } from '@/lib/format';
import { useBreadcrumbs } from '@/shell/breadcrumbs';
import { errorText } from '@/shell/errorText';
import { LoadError } from '@/shell/LoadError';
import { PageHeader } from '@/shell/PageHeader';
import {
  addVersion, archivePreset, buildQuery, buildRunning, buildVersion, patchCachedPreset, patchPreset, presetQuery, publishVersion, restorePreset, setFavorite,
  submitForReview,
} from './api';
import { STATUS_TONE, usePresetLabels } from './labels';
import { PresetArt } from './PresetArt';
import s from './preset.module.css';

const VERSION_TONE: Record<PresetVersion['status'], Tone> = { published: 'ok', review: 'warn', draft: 'mute' };
const BUILD_TONE: Record<string, Tone> = { ready: 'ok', failed: 'err' };

const when = (iso: string) => `${formatDateShort(iso)} · ${formatTime(iso)}`;

/**
 * /library/:presetId — the preset detail the TZ asks for (§7.2; DESIGN_SPEC §9 C6), additive, in the
 * design's parts: the picture and the live-preview frame, the build, the description, tags, licence
 * and attribution, the version list with review and publish (Admin), a new version from a prompt and
 * optional code, and the metadata drawer (layout, colour mode and mirror drive the wireframe).
 */
export function PresetPage() {
  const { presetId = '' } = useParams();
  const { t } = useTranslation('library');
  const { t: tc } = useTranslation('common');
  const { isAdmin } = useSession();
  const toast = useToast();
  const qc = useQueryClient();
  const labels = usePresetLabels();
  const q = useQuery(presetQuery(presetId));
  const p = q.data;
  const [editing, setEditing] = useState(false);
  const name = p ? labels.name(p) : '';
  useBreadcrumbs([{ label: t('page.title'), to: paths.library() }, ...(p ? [{ label: name }] : [])]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.preset(presetId) });
    void qc.invalidateQueries({ queryKey: ['presets'] });
  };
  const fail = (err: unknown) => toast(errorText(err, tc));
  const favorite = useMutation({
    mutationFn: (d: PresetDetail) => setFavorite(d.id, !d.is_favorite),
    onMutate: (d) => patchCachedPreset(qc, d.id, { is_favorite: !d.is_favorite }),
    onError: (err, d) => {
      patchCachedPreset(qc, d.id, { is_favorite: d.is_favorite });
      fail(err);
    },
  });
  const done = (message: string) => {
    refresh();
    toast(message);
  };
  const review = useMutation({ mutationFn: (v: PresetVersion) => submitForReview(v.id), onSuccess: (v) => done(t('detail.sent_to_review', { n: v.version })), onError: fail });
  const publish = useMutation({ mutationFn: (v: PresetVersion) => publishVersion(v.id), onSuccess: (v) => done(t('detail.published', { name, n: v.version })), onError: fail });
  const archive = useMutation({ mutationFn: () => archivePreset(presetId), onSuccess: () => done(t('detail.archived', { name })), onError: fail });
  const restore = useMutation({ mutationFn: () => restorePreset(presetId), onSuccess: () => done(t('detail.restored', { name })), onError: fail });

  if (q.isError) return <LoadError error={q.error} onRetry={() => void q.refetch()} />;
  if (!p) return null;

  const latest = p.versions.find((v) => v.id === p.latest_version_id) ?? p.versions[0];
  const archived = p.status === 'archived';
  const type = sectionType(p.section_type);
  const layoutLabel = isLayout(p.layout) ? t(`layouts.${p.layout}`) : p.layout;
  const eyebrow = [labels.section(p.section_type), layoutLabel, t(`modes.${p.color_mode}`), ...(p.mirrored ? [t('detail.mirrored')] : [])].join(' · ');
  const busy = review.isPending || publish.isPending;

  return (
    <>
      <PageHeader
        eyebrow={eyebrow}
        title={name}
        action={
          <div className={s.actions}>
            <Pill tone={STATUS_TONE[p.status]}>{t(`status.${p.status}`)}</Pill>
            <IconButton icon="star" label={t('card.favorite')} aria-pressed={p.is_favorite} className={cx(p.is_favorite && s.starOn)} onClick={() => favorite.mutate(p)} />
            {isAdmin && !archived && (
              <Button variant="secondary" icon="pencil" iconSize={14} onClick={() => setEditing(true)}>
                {t('detail.edit')}
              </Button>
            )}
            {isAdmin &&
              (archived ? (
                <Button variant="secondary" icon="rotate-ccw" iconSize={14} disabled={restore.isPending} onClick={() => restore.mutate()}>
                  {t('detail.restore')}
                </Button>
              ) : (
                <Button variant="secondary" icon="trash-2" iconSize={14} disabled={archive.isPending} onClick={() => archive.mutate()}>
                  {t('detail.archive')}
                </Button>
              ))}
            {isAdmin && !archived && latest && latest.status !== 'published' && (
              <Button icon="rocket" iconSize={15} disabled={busy} onClick={() => publish.mutate(latest)}>
                {t('detail.publish', { n: latest.version })}
              </Button>
            )}
          </div>
        }
      />
      <div className={s.layout}>
        <div className={s.main}>
          <Card padding={8}>
            <div className={s.cardHead}>
              <CapsLabel small>{t('detail.preview')}</CapsLabel>
              <span className={s.muted}>
                {latest?.preview_url
                  ? t('detail.preview_live')
                  : latest?.thumbnail_url
                    ? t('detail.preview_shot')
                    : p.thumbnail_url
                      ? t('detail.preview_source')
                      : t('detail.preview_wire')}
              </span>
            </div>
            <div className={s.previewFrame} data-testid="preset-preview">
              {latest?.preview_url ? (
                // The preview origin: a separate registrable domain without cookies (AC-12).
                <iframe className={s.previewIframe} src={latest.preview_url} title={t('detail.preview_live')} />
              ) : (
                <PresetArt preset={p} ratio={type.previewRatio} />
              )}
            </div>
            {latest?.scroll_video_url || latest?.source_video_url ? (
              // Our QA renderer's scroll video, else the catalog's own preview video (PLAN D33).
              <video
                className={s.scrollVideo}
                src={(latest.scroll_video_url || latest.source_video_url) ?? undefined}
                muted
                loop
                controls
                playsInline
                aria-label={t('detail.scroll_video')}
              />
            ) : (
              <div className={s.previewNote}>{t('detail.preview_pending')}</div>
            )}
          </Card>

          {latest && <BuildCard version={latest} canBuild={isAdmin && !archived} onStarted={refresh} onError={fail} />}

          {isAdmin && !archived && <NewVersion preset={p} onCreated={refresh} />}
        </div>

        <div className={s.side}>
          <Card className={s.stack}>
            <span className={s.title15}>{t('detail.about')}</span>
            {p.description && <p className={s.description}>{p.description}</p>}
            {(p.tags.length > 0 || p.style_tags.length > 0 || p.industries.length > 0) && (
              <div className={s.tags}>
                {[...p.tags, ...p.style_tags, ...p.industries].map((tag) => (
                  <Tag key={tag}>{tag}</Tag>
                ))}
              </div>
            )}
            <dl className={s.kv}>
              <Row label={t('detail.kv.section')} value={labels.section(p.section_type)} />
              <Row label={t('detail.kv.layout')} value={layoutLabel + (p.mirrored ? ` · ${t('detail.mirrored')}` : '')} />
              <Row label={t('detail.kv.mode')} value={t(`modes.${p.color_mode}`)} />
              <Row label={t('detail.kv.origin')} value={t(`detail.origin.${p.origin}`)} />
              <Row label={t('detail.kv.license')} value={p.license ?? latest?.license_spdx ?? t('detail.none')} mono={!!(p.license ?? latest?.license_spdx)} />
              <Row label={t('detail.kv.attribution')} value={p.attribution ?? latest?.copyright_line ?? t('detail.none')} />
              {p.source_url && (
                <Row
                  label={t('detail.kv.source')}
                  value={
                    <a href={p.source_url} target="_blank" rel="noreferrer noopener">
                      {p.source_url.replace(/^https?:\/\//, '')}
                    </a>
                  }
                />
              )}
              <Row label={t('detail.kv.updated')} value={when(p.updated_at)} />
            </dl>
          </Card>

          <Card padding={8}>
            <div className={s.cardHead}>
              <span className={s.title15}>{t('detail.versions')}</span>
              <span className={s.muted}>{t('detail.versions_count', { count: p.versions.length })}</span>
            </div>
            {p.versions.map((v) => (
              <div key={v.id} className={s.version} data-testid={`version-${v.version}`}>
                <div className={s.versionHead}>
                  <span className={s.versionNo}>{t('card.version', { n: v.version })}</span>
                  <Pill tone={VERSION_TONE[v.status]} size="tagTight">
                    {t(`detail.version_status.${v.status}`)}
                  </Pill>
                  {v.has_code && <span className={s.codeTag}>{t('detail.has_code')}</span>}
                  <span className={s.versionDate}>{v.published_at ? t('detail.published_at', { at: when(v.published_at) }) : when(v.created_at)}</span>
                </div>
                {v.prompt && <p className={s.versionPrompt}>{v.prompt}</p>}
                {(v.license_spdx || v.copyright_line) && <span className={s.versionMeta}>{[v.license_spdx, v.copyright_line].filter(Boolean).join(' · ')}</span>}
                {isAdmin && !archived && v.status !== 'published' && (
                  <div className={s.versionActions}>
                    {v.status === 'draft' && (
                      <Button variant="secondary" size={32} disabled={busy} onClick={() => review.mutate(v)}>
                        {t('detail.to_review')}
                      </Button>
                    )}
                    <Button variant="light" size={32} icon="check" iconSize={14} disabled={busy} onClick={() => publish.mutate(v)}>
                      {t('detail.publish_version')}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </Card>
        </div>
      </div>
      {editing && <EditPreset preset={p} onClose={() => setEditing(false)} />}
    </>
  );
}

function Row({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className={s.kvRow}>
      <dt className={s.kvKey}>{label}</dt>
      <dd className={cx(s.kvValue, mono && s.mono)}>{value}</dd>
    </div>
  );
}

/** A new version: the content of a version never changes, so an edit of the prompt or code is a new one. */
function NewVersion({ preset, onCreated }: { preset: PresetDetail; onCreated: () => void }) {
  const { t } = useTranslation('library');
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const latest = preset.versions[0];
  const [prompt, setPrompt] = useState('');
  const [code, setCode] = useState('');
  const [license, setLicense] = useState(latest?.license_spdx ?? '');
  const [copyright, setCopyright] = useState(latest?.copyright_line ?? '');
  const [problem, setProblem] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      addVersion(preset.id, {
        prompt: prompt.trim() || null,
        code: code.trim() || null,
        license_spdx: license.trim() || null,
        copyright_line: copyright.trim() || null,
        reference_media: [],
      }),
    onSuccess: (v) => {
      setPrompt('');
      setCode('');
      toast(t('detail.version_created', { n: v.version }));
      onCreated();
    },
    onError: (err) => setProblem(errorText(err, tc)),
  });
  const ok = !!(prompt.trim() || code.trim());
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setProblem(null);
    if (ok) create.mutate();
  };
  return (
    <Card className={s.stack}>
      <form className={s.stack} onSubmit={submit} noValidate>
        <span className={s.title15}>{t('detail.new_version')}</span>
        <span className={s.text13}>{t('detail.new_version_text')}</span>
        <Textarea label={t('detail.prompt')} mono rows={4} value={prompt} onChange={(e) => setPrompt(e.target.value)} />
        <Textarea label={t('detail.code')} mono rows={6} value={code} spellCheck={false} onChange={(e) => setCode(e.target.value)} />
        <div className={s.grid2}>
          <Input label={t('detail.license')} mono value={license} onChange={(e) => setLicense(e.target.value)} />
          <Input label={t('detail.copyright')} value={copyright} onChange={(e) => setCopyright(e.target.value)} />
        </div>
        <FieldHint>{t('detail.license_hint')}</FieldHint>
        <div className={s.formFoot}>
          {problem && (
            <span className={s.problem} role="alert">
              {problem}
            </span>
          )}
          <Button type="submit" size={38} icon="plus" iconSize={14} disabled={!ok || create.isPending}>
            {t('detail.create_version', { n: (latest?.version ?? 0) + 1 })}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** The metadata drawer: name, description, tags, and the layout, colour mode and mirror the wireframe shows. */
function EditPreset({ preset, onClose }: { preset: PresetDetail; onClose: () => void }) {
  const { t } = useTranslation('library');
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const qc = useQueryClient();
  const allowed = sectionType(preset.section_type).layouts;
  const [name, setName] = useState(preset.name);
  const [description, setDescription] = useState(preset.description ?? '');
  const [tags, setTags] = useState(preset.tags.join(', '));
  const [layout, setLayout] = useState(preset.layout);
  const [mode, setMode] = useState<ColorMode>(preset.color_mode);
  const [mirrored, setMirrored] = useState(preset.mirrored);
  const [problem, setProblem] = useState<string | null>(null);
  const canMirror = isLayout(layout) && MIRRORABLE.has(layout);
  const layouts: string[] = (allowed as string[]).includes(layout) ? allowed : [...allowed, layout];

  const save = useMutation({
    mutationFn: () => {
      const nextTags = tags.split(',').map((x) => x.trim()).filter(Boolean);
      const body: Parameters<typeof patchPreset>[1] = {};
      if (name.trim() !== preset.name) body.name = name.trim();
      if (description.trim() !== (preset.description ?? '')) body.description = description.trim() || null;
      if (nextTags.join('|') !== preset.tags.join('|')) body.tags = nextTags;
      if (layout !== preset.layout) body.layout = layout;
      if (mode !== preset.color_mode) body.color_mode = mode;
      const nextMirror = canMirror && mirrored;
      if (nextMirror !== preset.mirrored) body.mirrored = nextMirror;
      return patchPreset(preset.id, body);
    },
    onSuccess: (next) => {
      qc.setQueryData(qk.preset(preset.id), next);
      void qc.invalidateQueries({ queryKey: ['presets'] });
      toast(t('detail.saved'));
      onClose();
    },
    onError: (err) => setProblem(errorText(err, tc)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setProblem(null);
    if (name.trim()) save.mutate();
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={t('detail.edit_title')}
      sub={t('detail.edit_sub')}
      closeLabel={tc('actions.close_esc')}
      footerNote={
        problem && (
          <span className={s.problem} role="alert">
            {problem}
          </span>
        )
      }
      footer={
        <div className={s.drawerActions}>
          <Button size={38} variant="secondary" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button size={38} type="submit" form="preset-meta" disabled={save.isPending || !name.trim()}>
            {t('detail.save')}
          </Button>
        </div>
      }
    >
      <form id="preset-meta" className={s.stack} onSubmit={submit} noValidate>
        <Input label={t('detail.name')} value={name} onChange={(e) => setName(e.target.value)} />
        <Textarea label={t('detail.description')} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        <div className={s.group}>
          <Input label={t('detail.tags')} value={tags} onChange={(e) => setTags(e.target.value)} />
          <FieldHint>{t('detail.tags_hint')}</FieldHint>
        </div>
        <div className={s.group}>
          <span className={s.groupLabel}>{t('detail.kv.layout')}</span>
          <Segmented variant="compact" label={t('detail.kv.layout')} value={layout} onChange={setLayout} options={layouts.map((l) => ({ value: l, label: isLayout(l) ? t(`layouts.${l}`) : l }))} />
        </div>
        <div className={s.group}>
          <span className={s.groupLabel}>{t('detail.kv.mode')}</span>
          <Segmented variant="compact" label={t('detail.kv.mode')} value={mode} onChange={setMode} options={COLOR_MODES.map((m) => ({ value: m, label: t(`modes.${m}`) }))} />
        </div>
        <div className={s.group}>
          <span className={s.groupLabel}>{t('detail.mirror')}</span>
          <Segmented
            variant="compact"
            label={t('detail.mirror')}
            value={canMirror && mirrored ? 'on' : 'off'}
            onChange={(v) => setMirrored(v === 'on')}
            options={[
              { value: 'off', label: t('detail.mirror_off') },
              { value: 'on', label: t('detail.mirror_on'), disabled: !canMirror },
            ]}
          />
        </div>
        <div className={s.group}>
          <span className={s.groupLabel}>{t('detail.wire_preview')}</span>
          <Thumb layout={layout} mode={mode} mirrored={canMirror && mirrored} ratio={sectionType(preset.section_type).galleryRatio} className={s.editThumb} />
          <FieldHint>{t('detail.wire_hint')}</FieldHint>
        </div>
      </form>
    </Drawer>
  );
}

/** The version's sandbox build (P3-D02): status, the builder's problems with their fix hints, and
 * «Собрать в sandbox» (Admin; the version needs code). The page polls while it runs. */
function BuildCard({ version, canBuild, onStarted, onError }: { version: PresetVersion; canBuild: boolean; onStarted: () => void; onError: (e: unknown) => void }) {
  const { t } = useTranslation('library');
  const build = version.build;
  const failed = build?.status === 'failed';
  const details = useQuery({ ...buildQuery(build?.id ?? ''), enabled: failed });
  const run = useMutation({ mutationFn: () => buildVersion(version.id), onSuccess: onStarted, onError });
  const running = buildRunning(build?.status);
  const problems = details.data?.errors ?? [];
  return (
    <Card className={s.stack}>
      <div className={s.rowBetween}>
        <span className={s.title15}>{t('detail.build')}</span>
        <Pill tone={build ? BUILD_TONE[build.status] ?? 'warn' : 'mute'} size="tagTight">
          {build ? t(`detail.build_status.${build.status}`, { defaultValue: build.status }) : t('detail.build_none')}
        </Pill>
      </div>
      <span className={s.text13}>{t('detail.build_text')}</span>
      <pre className={s.logs} data-testid="build-log">
        {failed
          ? problems.length
            ? problems.map((e) => `${e.file ?? ''}${e.line ? `:${e.line}` : ''}  ${e.message ?? ''}${e.hint ? `\n  → ${e.hint}` : ''}`).join('\n')
            : t('detail.build_failed_code', { code: build?.error_code ?? '' })
          : build?.status === 'ready'
            ? t('detail.build_ready')
            : running
              ? t('detail.build_running')
              : t('detail.build_logs_empty')}
      </pre>
      {canBuild && (
        <Button
          variant="secondary"
          size={36}
          icon="hammer"
          iconSize={14}
          className={s.alignStart}
          disabled={!version.has_code || running || run.isPending}
          title={version.has_code ? undefined : t('detail.build_needs_code')}
          onClick={() => run.mutate()}
        >
          {build ? t('detail.build_again') : t('detail.build_run')}
        </Button>
      )}
    </Card>
  );
}
